import React from 'react';
import { Database, CheckCircle2, Clock, Shield, ExternalLink, Activity, CloudRain } from 'lucide-react';
import { APP_CONFIG } from '../config/appConfig';

export const DataSourcesPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black text-navy-950">
          Verified Disruption Data Sources &amp; Pipeline
        </h1>
        <p className="text-base text-slate-600 mt-2 leading-relaxed">
          SignalNear publishes only verified, authoritative sources that have an active, tested data integration.
        </p>
      </div>

      {/* VERIFIED INTEGRATION 1: NOAA NWS */}
      <section className="bg-white border-2 border-emerald-500/40 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center border border-emerald-200">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-950">
                1. US National Weather Service (NWS / NOAA)
              </h2>
              <span className="text-xs font-mono font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                ACTIVE &amp; OPERATIONAL
              </span>
            </div>
          </div>

          <a
            href="https://www.weather.gov/documentation/services-web-api"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            <span>Official NWS API Docs</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-700 pt-2">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">API Base &amp; Endpoint:</span>
            <code className="text-blue-700 font-mono">https://api.weather.gov/alerts/active</code>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Data Protocol:</span>
            <span>Common Alerting Protocol (CAP v1.2) via GeoJSON</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Coverage Area:</span>
            <span>All 50 US States, DC, Puerto Rico, US Marine Zones</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Licensing / Terms:</span>
            <span>U.S. Public Domain (U.S. Federal Government Work)</span>
          </div>
        </div>
      </section>

      {/* VERIFIED INTEGRATION 2: USGS EARTHQUAKES */}
      <section className="bg-white border-2 border-purple-500/40 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center border border-purple-200">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-950">
                2. US Geological Survey (USGS Earthquake Hazards)
              </h2>
              <span className="text-xs font-mono font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                ACTIVE &amp; OPERATIONAL
              </span>
            </div>
          </div>

          <a
            href="https://earthquake.usgs.gov/earthquakes/feed/v1.0/geojson.php"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-semibold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            <span>Official USGS Feed Docs</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-700 pt-2">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Feed Endpoint:</span>
            <code className="text-purple-700 font-mono">https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson</code>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Threshold Ingested:</span>
            <span>Magnitude 2.5+ Seismic Events (Past 24 Hours)</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Geographic Coverage:</span>
            <span>United States National &amp; Global Seismic Network</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Licensing / Copyright:</span>
            <span>U.S. Public Domain (USGS Open Data Policy)</span>
          </div>
        </div>
      </section>

      {/* VERIFIED INTEGRATION 3: RAINVIEWER DOPPLER RADAR TILES */}
      <section className="bg-white border-2 border-cyan-500/40 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-50 text-cyan-600 rounded-xl flex items-center justify-center border border-cyan-200">
              <CloudRain className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-950">
                3. RainViewer Real-Time Precipitation Doppler Radar
              </h2>
              <span className="text-xs font-mono font-semibold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded border border-cyan-200">
                ACTIVE &amp; OPERATIONAL
              </span>
            </div>
          </div>

          <a
            href="https://www.rainviewer.com/api.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-cyan-600 hover:text-cyan-800 font-semibold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            <span>RainViewer API</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
          Provides real-time mosaic Doppler radar tiles rendered directly onto the geospatial radar map as a semi-transparent precipitation overlay. Updated every 10 minutes from raw meteorological radar sweeps across the US, Canada, the UK, and Australia.
        </p>
      </section>

      {/* VERIFIED INTEGRATION 4: ENVIRONMENT AND CLIMATE CHANGE CANADA (ECCC) */}
      <section className="bg-white border-2 border-red-500/40 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center border border-red-200">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-950">
                4. Environment and Climate Change Canada (ECCC GeoMet)
              </h2>
              <span className="text-xs font-mono font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                ACTIVE &amp; OPERATIONAL
              </span>
            </div>
          </div>

          <a
            href="https://weather.gc.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            <span>ECCC GeoMet Portal</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-700 pt-2">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">GeoMet Endpoint:</span>
            <code className="text-blue-700 font-mono">https://api.weather.gc.ca/collections/bulletins-realtime</code>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Coverage Area:</span>
            <span>All 10 Canadian Provinces &amp; 3 Territories</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Licensing:</span>
            <span>Open Government Licence - Canada</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Update Cadence:</span>
            <span>Continuous Real-Time Push Ingestion</span>
          </div>
        </div>
      </section>

      {/* VERIFIED INTEGRATION 5: UK ENVIRONMENT AGENCY */}
      <section className="bg-white border-2 border-teal-500/40 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center border border-teal-200">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-950">
                5. UK Environment Agency (Flood &amp; Coastal Hazards)
              </h2>
              <span className="text-xs font-mono font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                ACTIVE &amp; OPERATIONAL
              </span>
            </div>
          </div>

          <a
            href="https://environment.data.gov.uk/flood-monitoring/doc/reference"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            <span>data.gov.uk Flood API</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-700 pt-2">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">API Endpoint:</span>
            <code className="text-teal-700 font-mono">https://environment.data.gov.uk/flood-monitoring/id/floods</code>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Coverage Area:</span>
            <span>England, Wales &amp; UK Coastal Inundation Zones</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Licensing:</span>
            <span>Open Government Licence v3.0 (OGL)</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
            <span className="font-bold text-navy-900 block font-mono">Update Cadence:</span>
            <span>Updated every 15 minutes</span>
          </div>
        </div>
      </section>

      {/* ROADMAP: UPCOMING FREE OFFICIAL SOURCES */}
      <section className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-500" />
          <h2 className="text-lg font-bold text-navy-950">
            Official Data Sources Expansion Roadmap
          </h2>
        </div>
        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
          The following official government feeds are scheduled for staged integration once edge collection budgets and data normalization schemas are certified:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">NOAA Storm Prediction Center (SPC)</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Tornado &amp; Hail Reports</div>
            <p className="text-slate-500 text-[11px]">
              Raw preliminary local storm reports (LSR) as logged in real-time by storm spotters.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">National Hurricane Center (NHC)</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Tropical Cones &amp; Tracks</div>
            <p className="text-slate-500 text-[11px]">
              Active tropical storm and hurricane forecast cones, track waypoints, and wind speed probabilities.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">NASA FIRMS / NIFC</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Wildfire Hotspots</div>
            <p className="text-slate-500 text-[11px]">
              MODIS and VIIRS satellite active fire thermal anomaly detections and perimeter boundaries.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">EPA AirNow API</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Air Quality Index (AQI)</div>
            <p className="text-slate-500 text-[11px]">
              Current and forecast PM2.5, ozone, and wildfire smoke particulate concentrations.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">OpenFEMA API</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Disaster Declarations</div>
            <p className="text-slate-500 text-[11px]">
              Presidential major disaster declarations, emergency designations, and public assistance summaries.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <div className="font-bold text-navy-900">State 511 / WZDx</div>
            <div className="text-[11px] text-blue-600 font-semibold font-mono">Highway Closures</div>
            <p className="text-slate-500 text-[11px]">
              State Department of Transportation road closures and emergency highway detours.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
